import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import HomeIcon from "@mui/icons-material/Home";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SettingsIcon from "@mui/icons-material/Settings";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

/**
 * Sidebar navigation component matching WeGuide-UI Jason design.
 * Replaces the previous top Menubar with a left sidebar.
 *
 * Features:
 * - Collapsible on desktop (icon-only mode)
 * - Drawer on mobile (slides from left)
 * - Auth-aware (login/logout, my assessments)
 * - Dark mode toggle
 * - Active route highlighting
 */
function Sidebar({ darkMode, toggleDarkMode }) {
  const auth = getAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [isAuth, setIsAuth] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [isOpen, setIsOpen] = useState(false); // mobile drawer
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem("sidebar-collapsed") === "true";
  });

  const navItems = [
    { text: "Home", path: "/", icon: <HomeIcon /> },
    { text: "Program", path: "/program", icon: <ContentPasteIcon /> },
    { text: "Resources", path: "/resources", icon: <MenuBookIcon /> },
    { text: "FAQ", path: "/faq", icon: <HelpOutlineIcon /> },
  ];

  const isMobile = useCallback(() => window.innerWidth < 1024, []);

  // Auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setIsAuth(true);
        setUserEmail(user.email || "");
      } else {
        setIsAuth(false);
        setUserEmail("");
      }
    });
    return () => unsubscribe();
  }, [auth]);

  // Close mobile drawer on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (!isMobile() && isOpen) {
        setIsOpen(false);
        document.body.style.overflow = "";
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isOpen, isMobile]);

  // Close mobile drawer on escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        closeSidebar();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem("sidebar-collapsed", String(isCollapsed));
  }, [isCollapsed]);

  const openSidebar = () => {
    setIsOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeSidebar = () => {
    setIsOpen(false);
    document.body.style.overflow = "";
  };

  const toggleCollapse = () => {
    if (isMobile()) {
      if (isOpen) closeSidebar();
      else openSidebar();
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  const handleLogin = () => {
    localStorage.removeItem("hasSeenLoginPrompt");
    localStorage.removeItem("promptTimestamp");
    window.dispatchEvent(new Event("openLoginPrompt"));
    closeSidebar();
  };

  const handleLogout = () => {
    signOut(auth)
      .then(() => {
        if (location.pathname === "/myAssessments") {
          navigate("/home");
        }
      })
      .catch((error) => {
        console.error("Error logging out:", error);
      });
    closeSidebar();
  };

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/" || location.pathname === "/home" || location.pathname === "/catalog";
    if (path === "/resources") return location.pathname.startsWith("/resources");
    return location.pathname === path;
  };

  return (
    <>
      {/* Mobile overlay */}
      <div
        className={`sidebar-overlay ${isOpen ? "active" : ""}`}
        onClick={closeSidebar}
      />

      {/* Mobile toggle button (floating) */}
      <button
        className="sidebar-mobile-toggle lg:hidden"
        onClick={openSidebar}
        aria-label="Open navigation"
      >
        <MenuIcon sx={{ width: 20, height: 20 }} />
      </button>

      {/* Sidebar */}
      <aside
        className={`sidebar ${isCollapsed ? "collapsed" : ""} ${isOpen ? "open" : ""}`}
      >
        {/* Header */}
        <div className="sidebar-header">
          <Link to="/" className="sidebar-logo" onClick={closeSidebar} style={{ textDecoration: "none" }}>
            <div className="sidebar-logo-icon">
              <img src="/logoNoName.png" alt="WeGuide" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit" }} />
            </div>
            <span>WeGuide</span>
          </Link>

          {/* Collapse button (desktop) / Close button (mobile) */}
          <button
            className="sidebar-collapse-btn"
            onClick={toggleCollapse}
            aria-label={isMobile() ? "Close sidebar" : (isCollapsed ? "Expand sidebar" : "Collapse sidebar")}
          >
            <span className="lg:hidden">
              <CloseIcon sx={{ width: 18, height: 18 }} />
            </span>
            <span className="hidden lg:inline-flex">
              {isCollapsed ? (
                <ChevronRightIcon sx={{ width: 18, height: 18 }} />
              ) : (
                <ChevronLeftIcon sx={{ width: 18, height: 18 }} />
              )}
            </span>
          </button>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-nav-item ${isActive(item.path) ? "active" : ""}`}
              data-tooltip={item.text}
              onClick={closeSidebar}
              style={{ textDecoration: "none" }}
            >
              <span className="sidebar-nav-item-icon">{item.icon}</span>
              <span className="flex-1">{item.text}</span>
            </Link>
          ))}

          {/* My Assessments hidden while login is disabled */}
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          {/* Dark mode toggle */}
          <button
            className="sidebar-nav-item"
            onClick={toggleDarkMode}
            data-tooltip={darkMode ? "Light mode" : "Dark mode"}
          >
            <span className="sidebar-nav-item-icon">
              {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </span>
            <span className="flex-1">{darkMode ? "Light Mode" : "Dark Mode"}</span>
          </button>

          {/* Login/Logout and user profile hidden for now */}
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
